import { inventoryManagementService } from './inventoryManagementService';
import { components } from '@smartphone-tycoon/shared-core';
import type { AutoOrderSetting, ComponentOrder } from '@smartphone-tycoon/shared-core';
import type { ComponentInventory } from '../../stores/inventoryStore';
import type { GameDate } from '../../types/events';

interface AutoOrderResult {
  orders: ComponentOrder[];
  totalCost: number;
  updatedSettings: Map<string, AutoOrderSetting>;
}

class AutoOrderService {
  private static instance: AutoOrderService;
  // Cached Map for component costs to avoid repeated nested loop lookups
  private componentCostCache: Map<string, number> | null = null;

  private constructor() {}

  static getInstance(): AutoOrderService {
    if (!AutoOrderService.instance) {
      AutoOrderService.instance = new AutoOrderService();
    }
    return AutoOrderService.instance;
  }

  /**
   * Build and cache the component cost lookup Map
   */
  private getComponentCostCache(): Map<string, number> {
    if (!this.componentCostCache) {
      this.componentCostCache = new Map();
      for (const category of components) {
        for (const opt of category.options) {
          this.componentCostCache.set(opt.value, opt.cost);
        }
      }
    }
    return this.componentCostCache;
  }

  /**
   * Find component cost by component ID from the cached Map
   */
  private findComponentCost(componentId: string): number {
    return this.getComponentCostCache().get(componentId) ?? 0;
  }

  /**
   * Check if it's the first day of a period based on frequency
   */
  private isFirstDayOfPeriod(gameDate: GameDate, frequency: 'month' | 'quarter' | 'year'): boolean {
    if (gameDate.day !== 1) return false;

    switch (frequency) {
      case 'month':
        return true;
      case 'quarter':
        // Quarters start in Jan (1), Apr (4), Jul (7), Oct (10)
        return [1, 4, 7, 10].includes(gameDate.month);
      case 'year':
        return gameDate.month === 1;
      default:
        return false;
    }
  }

  /**
   * Check if an order was already placed this period
   */
  private wasOrderedThisPeriod(
    lastOrderDate: GameDate | undefined,
    currentDate: GameDate,
    frequency: 'month' | 'quarter' | 'year'
  ): boolean {
    if (!lastOrderDate) return false;

    switch (frequency) {
      case 'month':
        return lastOrderDate.year === currentDate.year && lastOrderDate.month === currentDate.month;
      case 'quarter': {
        const currentQuarter = Math.ceil(currentDate.month / 3);
        const lastQuarter = Math.ceil(lastOrderDate.month / 3);
        return lastOrderDate.year === currentDate.year && currentQuarter === lastQuarter;
      }
      case 'year':
        return lastOrderDate.year === currentDate.year;
      default:
        return false;
    }
  }

  /**
   * Process auto-orders for components at or below their threshold
   * Uses the new autoOrderThreshold field (null = disabled)
   * @param pendingOrders - List of pending orders to check against to prevent duplicate ordering
   */
  async processThresholdAutoOrders(
    gameDate: GameDate,
    componentInventory: Map<string, ComponentInventory>,
    autoOrderSettings: Map<string, AutoOrderSetting>,
    availableCash: number,
    pendingOrders: ComponentOrder[] = []
  ): Promise<AutoOrderResult> {
    const orders: ComponentOrder[] = [];
    let totalCost = 0;
    const updatedSettings = new Map(autoOrderSettings);
    let remainingCash = availableCash;

    for (const [componentId, setting] of autoOrderSettings) {
      // Skip if threshold is null (disabled)
      if (setting.autoOrderThreshold === null || setting.autoOrderThreshold === undefined) continue;

      // Skip if there's already a pending order for this component
      const hasPendingOrder = pendingOrders.some(order => order.componentId === componentId);
      if (hasPendingOrder) continue;

      // Check if component is at or below threshold
      const inventory = componentInventory.get(componentId);
      const currentQuantity = inventory?.quantity ?? 0;
      if (currentQuantity > setting.autoOrderThreshold) continue;

      // Calculate cost
      const unitCost = this.findComponentCost(componentId);
      const orderCost = unitCost * setting.quantity;

      // Skip if not enough funds
      if (orderCost > remainingCash) continue;

      try {
        // Place the order
        const order = await inventoryManagementService.placeComponentOrder(
          componentId,
          setting.quantity,
          gameDate
        );

        orders.push(order);
        totalCost += orderCost;
        remainingCash -= orderCost;
      } catch (error) {
        console.error(`Failed to place threshold auto-order for ${componentId}:`, error);
      }
    }

    return { orders, totalCost, updatedSettings };
  }

  /**
   * Process auto-orders for components with zero inventory
   * @deprecated Use processThresholdAutoOrders instead
   */
  async processZeroInventoryAutoOrders(
    gameDate: GameDate,
    componentInventory: Map<string, ComponentInventory>,
    autoOrderSettings: Map<string, AutoOrderSetting>,
    availableCash: number
  ): Promise<AutoOrderResult> {
    const orders: ComponentOrder[] = [];
    let totalCost = 0;
    const updatedSettings = new Map(autoOrderSettings);
    let remainingCash = availableCash;

    for (const [componentId, setting] of autoOrderSettings) {
      // Skip if not configured for zero inventory ordering
      if (!setting.orderOnZeroInventory) continue;

      // Check if component has zero stock
      const inventory = componentInventory.get(componentId);
      if (!inventory || inventory.quantity > 0) continue;

      // Calculate cost
      const unitCost = this.findComponentCost(componentId);
      const orderCost = unitCost * setting.quantity;

      // Skip if not enough funds
      if (orderCost > remainingCash) continue;

      try {
        // Place the order
        const order = await inventoryManagementService.placeComponentOrder(
          componentId,
          setting.quantity,
          gameDate
        );

        orders.push(order);
        totalCost += orderCost;
        remainingCash -= orderCost;
      } catch (error) {
        console.error(`Failed to place auto-order for ${componentId}:`, error);
      }
    }

    return { orders, totalCost, updatedSettings };
  }

  /**
   * Process recurring auto-orders based on cadence (monthly, quarterly, yearly)
   */
  async processRecurringAutoOrders(
    gameDate: GameDate,
    autoOrderSettings: Map<string, AutoOrderSetting>,
    availableCash: number
  ): Promise<AutoOrderResult> {
    const orders: ComponentOrder[] = [];
    let totalCost = 0;
    const updatedSettings = new Map(autoOrderSettings);
    let remainingCash = availableCash;

    for (const [componentId, setting] of autoOrderSettings) {
      // Skip if recurring order is not enabled
      if (!setting.recurringOrder.enabled) continue;

      // Check if it's the right day for this frequency
      if (!this.isFirstDayOfPeriod(gameDate, setting.recurringOrder.frequency)) continue;

      // Check if we already ordered this period
      if (this.wasOrderedThisPeriod(
        setting.recurringOrder.lastOrderDate,
        gameDate,
        setting.recurringOrder.frequency
      )) continue;

      // Calculate cost
      const unitCost = this.findComponentCost(componentId);
      const orderCost = unitCost * setting.quantity;

      // Skip if not enough funds
      if (orderCost > remainingCash) continue;

      try {
        // Place the order
        const order = await inventoryManagementService.placeComponentOrder(
          componentId,
          setting.quantity,
          gameDate
        );

        orders.push(order);
        totalCost += orderCost;
        remainingCash -= orderCost;

        // Update the last order date
        const updatedSetting: AutoOrderSetting = {
          ...setting,
          recurringOrder: {
            ...setting.recurringOrder,
            lastOrderDate: { ...gameDate },
          },
        };
        updatedSettings.set(componentId, updatedSetting);
      } catch (error) {
        console.error(`Failed to place recurring order for ${componentId}:`, error);
      }
    }

    return { orders, totalCost, updatedSettings };
  }

  /**
   * Process all auto-orders (threshold-based, zero inventory, and recurring)
   */
  async processAllAutoOrders(
    gameDate: GameDate,
    componentInventory: Map<string, ComponentInventory>,
    autoOrderSettings: Map<string, AutoOrderSetting>,
    availableCash: number
  ): Promise<AutoOrderResult> {
    const allOrders: ComponentOrder[] = [];
    let totalCost = 0;
    let remainingCash = availableCash;
    const updatedSettings = new Map(autoOrderSettings);

    // Fetch pending orders to prevent duplicate threshold-based orders
    const pendingOrders = await inventoryManagementService.getPendingOrders();

    // Process threshold-based orders first (new method)
    const thresholdResult = await this.processThresholdAutoOrders(
      gameDate,
      componentInventory,
      autoOrderSettings,
      remainingCash,
      pendingOrders
    );
    allOrders.push(...thresholdResult.orders);
    totalCost += thresholdResult.totalCost;
    remainingCash -= thresholdResult.totalCost;

    // Process zero inventory orders (deprecated, for backwards compatibility)
    const zeroInventoryResult = await this.processZeroInventoryAutoOrders(
      gameDate,
      componentInventory,
      autoOrderSettings,
      remainingCash
    );
    allOrders.push(...zeroInventoryResult.orders);
    totalCost += zeroInventoryResult.totalCost;
    remainingCash -= zeroInventoryResult.totalCost;

    // Process recurring orders
    const recurringResult = await this.processRecurringAutoOrders(
      gameDate,
      autoOrderSettings,
      remainingCash
    );
    allOrders.push(...recurringResult.orders);
    totalCost += recurringResult.totalCost;

    // Merge updated settings (recurring orders update lastOrderDate)
    for (const [componentId, setting] of recurringResult.updatedSettings) {
      updatedSettings.set(componentId, setting);
    }

    return { orders: allOrders, totalCost, updatedSettings };
  }
}

export const autoOrderService = AutoOrderService.getInstance();
